import type { Candle, Timeframe } from '@/types'

const BINANCE_FUTURES_API = 'https://fapi.binance.com/fapi/v1'

// Timeframe to Binance interval mapping
const INTERVAL_MAP: Record<Timeframe, string> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '1h': '1h',
  '4h': '4h',
  '1d': '1d',
}

// Binance kline response format
// [openTime, open, high, low, close, volume, closeTime, quoteVolume, trades, buyBaseVolume, buyQuoteVolume, ignore]
type BinanceKline = [
  number, // Open time
  string, // Open
  string, // High
  string, // Low
  string, // Close
  string, // Volume
  number, // Close time
  string, // Quote asset volume
  number, // Number of trades
  string, // Taker buy base asset volume
  string, // Taker buy quote asset volume
  string, // Ignore
]

/**
 * Fetch candle data from Binance Futures API
 */
export async function fetchCandles(
  symbol: string,
  timeframe: Timeframe,
  limit = 500
): Promise<Candle[]> {
  const interval = INTERVAL_MAP[timeframe]
  const url = `${BINANCE_FUTURES_API}/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch candles: ${response.statusText}`)
  }

  const data: BinanceKline[] = await response.json()

  return data.map((kline) => ({
    time: Math.floor(kline[0] / 1000), // Convert to seconds for lightweight-charts
    open: parseFloat(kline[1]),
    high: parseFloat(kline[2]),
    low: parseFloat(kline[3]),
    close: parseFloat(kline[4]),
    volume: parseFloat(kline[5]),
  }))
}

/**
 * Fetch historical candles with start/end time
 */
export async function fetchCandlesRange(
  symbol: string,
  timeframe: Timeframe,
  startTime: number,
  endTime: number
): Promise<Candle[]> {
  const interval = INTERVAL_MAP[timeframe]
  const url = `${BINANCE_FUTURES_API}/klines?symbol=${symbol}&interval=${interval}&startTime=${startTime}&endTime=${endTime}&limit=1500`

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch candles: ${response.statusText}`)
  }

  const data: BinanceKline[] = await response.json()

  return data.map((kline) => ({
    time: Math.floor(kline[0] / 1000),
    open: parseFloat(kline[1]),
    high: parseFloat(kline[2]),
    low: parseFloat(kline[3]),
    close: parseFloat(kline[4]),
    volume: parseFloat(kline[5]),
  }))
}

/**
 * Get current price from Binance
 */
export async function getCurrentPrice(symbol: string): Promise<number> {
  const url = `${BINANCE_FUTURES_API}/ticker/price?symbol=${symbol}`

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch price: ${response.statusText}`)
  }

  const data = await response.json()
  return parseFloat(data.price)
}
